import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings as SettingsIcon, Save, Loader2, Plus, Plug, ShieldCheck, KeyRound, Trash2,
  Building2, Megaphone, Users2, ScrollText, DollarSign, CheckCircle2, XCircle,
  Rocket, Circle, AlertTriangle, ShieldAlert, ChevronRight, Lock, Sparkles,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo, titleCase, fmtMoney, ROLE_BADGE } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { ThemesSettings } from "@/components/app/ThemesSettings";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export default function SettingsPage() {
  const { hasRole } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || (hasRole("admin") ? "golive" : "compliance"));
  return (
    <div>
      <PageHeader title="Settings" subtitle="Go-live readiness, compliance, campaigns, integrations, team, and AI cost." icon={SettingsIcon} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
          {hasRole("admin") && <Tab v="golive" icon={Rocket} label="Go-Live" />}
          <Tab v="compliance" icon={Building2} label="Compliance" />
          <Tab v="campaigns" icon={Megaphone} label="Campaigns" />
          <Tab v="themes" icon={Sparkles} label="Themes" />
          <Tab v="integrations" icon={Plug} label="Integrations" />
          {hasRole("admin") && <Tab v="team" icon={Users2} label="Team" />}
          {hasRole("admin", "manager") && <Tab v="activity" icon={ScrollText} label="Activity" />}
          {hasRole("admin", "manager") && <Tab v="cost" icon={DollarSign} label="AI Cost" />}
        </TabsList>
        {hasRole("admin") && <TabsContent value="golive"><GoLive goto={setTab} /></TabsContent>}
        <TabsContent value="compliance">
          <div className="space-y-6">
            <Compliance />
            {hasRole("admin", "manager") && <ConsentLog />}
          </div>
        </TabsContent>
        <TabsContent value="campaigns"><Campaigns /></TabsContent>
        <TabsContent value="themes"><ThemesSettings /></TabsContent>
        <TabsContent value="integrations"><Integrations /></TabsContent>
        <TabsContent value="team"><Team /></TabsContent>
        <TabsContent value="activity"><Activity /></TabsContent>
        <TabsContent value="cost"><Cost /></TabsContent>
      </Tabs>
    </div>
  );
}

function Tab({ v, icon: Icon, label }) {
  return (
    <TabsTrigger value={v} className="gap-1.5 rounded-lg border border-transparent px-3 py-2 data-[state=active]:border-brand-red/20 data-[state=active]:bg-brand-red/10 data-[state=active]:text-brand-red data-[state=active]:shadow-none">
      <Icon className="h-4 w-4" /> {label}
    </TabsTrigger>
  );
}

function Card({ children, className, ...props }) {
  return <div className={cn("rounded-xl border border-border bg-white p-6 shadow-sm", className)} {...props}>{children}</div>;
}

/* ---------- Go-Live readiness ---------- */
const STATUS_META = {
  done: { icon: CheckCircle2, color: "text-pos-text", ring: "border-pos-border" },
  todo: { icon: Circle, color: "text-brand-red", ring: "border-brand-red/30" },
  warn: { icon: AlertTriangle, color: "text-neu-text", ring: "border-neu-border" },
  optional: { icon: Circle, color: "text-slate-400", ring: "border-border" },
};
const SEV_BADGE = {
  critical: "bg-neg-bg text-neg-text",
  high: "bg-neu-bg text-neu-text",
  medium: "bg-brand-blue/10 text-brand-blue",
  info: "bg-slate-100 text-slate-500",
};

function GoLive({ goto }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [newItem, setNewItem] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [rescoping, setRescoping] = useState(false);
  const [rescopeLog, setRescopeLog] = useState([]);
  const { data, isLoading } = useQuery({ queryKey: ["readiness"], queryFn: () => api.get("/admin/readiness").then((r) => r.data) });

  const refresh = () => qc.invalidateQueries({ queryKey: ["readiness"] });

  const addItem = async () => {
    if (!newItem.trim()) return;
    await api.post("/admin/readiness/items", { title: newItem.trim() });
    setNewItem("");
    refresh();
  };
  const toggleItem = async (it) => { await api.patch(`/admin/readiness/items/${it.id}`, { done: !it.done }); refresh(); };
  const delItem = async (id) => { await api.delete(`/admin/readiness/items/${id}`); refresh(); };

  const resetDemo = async () => {
    if (!window.confirm("Remove all seeded demo data (feed, synthetic voters, demo contacts, canvass)? Users, compliance and integrations are kept. This cannot be undone.")) return;
    try {
      const { data: res } = await api.post("/admin/reset-demo-data");
      toast.success("Demo data cleared");
      qc.invalidateQueries();
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rescope = async () => {
    if (!window.confirm("Re-scope this workspace to MI-04 (McCann vs Huizenga)? Seeded demo content will be removed. This cannot be undone.")) return;
    setRescoping(true);
    try {
      const { data: res } = await api.post("/admin/rescope-mi04", { confirm });
      setRescopeLog(res.log || []);
      toast.success("Workspace re-scoped to MI-04, pulling live FEC + press data now");
      setConfirm("");
      qc.invalidateQueries();
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setRescoping(false); }
  };

  const runAction = (action) => {    if (!action || action.type === "none") return;
    if (action.type === "tab") goto(action.tab);
    else if (action.type === "page") navigate(action.to);
    else if (action.type === "change_password") setPwOpen(true);
    else if (action.type === "reset_demo") resetDemo();
  };

  if (isLoading || !data) return <Loader2 className="mt-6 h-5 w-5 animate-spin text-brand-red" />;

  const pct = data.percent;
  const ringColor = data.production_ready ? "#1E7E34" : pct >= 60 ? "#B8860B" : "#E63946";

  return (
    <div className="space-y-5">
      {/* Score header */}
      <Card className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
        <div className="relative h-28 w-28 shrink-0">
          <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E8F0" strokeWidth="10" />
            <circle cx="50" cy="50" r="42" fill="none" stroke={ringColor} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={`${(pct / 100) * 264} 264`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-heading text-2xl font-extrabold text-brand-ink">{pct}%</span>
            <span className="text-[0.65rem] text-brand-mute">ready</span>
          </div>
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h2 className="font-heading text-2xl font-extrabold text-brand-ink">Production readiness</h2>
          <p className="mt-1 text-sm text-brand-mute">{data.done} of {data.total} items complete.</p>
          {data.production_ready ? (
            <Badge className="mt-3 bg-pos-bg text-pos-text"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> No critical blockers remaining</Badge>
          ) : (
            <Badge className="mt-3 bg-neg-bg text-neg-text"><ShieldAlert className="mr-1 h-3.5 w-3.5" /> {data.blockers} critical blocker{data.blockers === 1 ? "" : "s"} to resolve</Badge>
          )}
        </div>
      </Card>

      {/* Checks */}
      <Card className="p-0">
        <div className="border-b border-border px-6 py-4"><h3 className="font-heading text-lg font-bold text-brand-ink">Pre-production checklist</h3></div>
        <div className="divide-y divide-border">
          {data.checks.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.todo;
            const Icon = meta.icon;
            return (
              <div key={c.key} className="flex items-start gap-4 px-6 py-4" data-testid={`check-${c.key}`}>
                <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", meta.color)} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-brand-ink">{c.title}</p>
                    <Badge className={cn("text-[0.6rem] uppercase", SEV_BADGE[c.severity])}>{c.severity}</Badge>
                    <Badge variant="outline" className="text-[0.6rem]">{c.category}</Badge>
                  </div>
                  <p className="mt-0.5 text-sm text-brand-mute">{c.description}</p>
                  {Array.isArray(c.value) && c.value.length > 0 && (
                    <p className="mt-1 text-xs text-brand-red">{c.value.join(", ")}</p>
                  )}
                </div>
                {c.status !== "done" && c.action?.type !== "none" && (
                  <Button variant="outline" size="sm" className="shrink-0" onClick={() => runAction(c.action)} data-testid={`fix-${c.key}`}>
                    {c.action.label || "Fix"} <ChevronRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Custom todos */}
      <Card>
        <h3 className="font-heading text-lg font-bold text-brand-ink">Your go-live to-dos</h3>
        <p className="text-sm text-brand-mute">Track anything else your team needs before launch.</p>
        <div className="mt-4 flex gap-2">
          <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()}
            placeholder="e.g. Configure MongoDB Atlas backups" data-testid="new-todo-input" />
          <Button onClick={addItem} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="add-todo-btn"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="mt-4 space-y-2">
          {data.custom_items.length === 0 && <p className="text-sm text-brand-mute">No custom items yet.</p>}
          {data.custom_items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid="todo-item">
              <Checkbox checked={it.done} onCheckedChange={() => toggleItem(it)} data-testid={`toggle-todo-${it.id}`} />
              <span className={cn("flex-1 text-sm", it.done ? "text-brand-mute line-through" : "text-brand-ink")}>{it.title}</span>
              <Button variant="ghost" size="icon" onClick={() => delItem(it.id)}><Trash2 className="h-4 w-4 text-brand-red" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="font-heading text-lg font-bold text-brand-ink">Re-scope this workspace to MI-04</h3>
        <p className="mt-1 text-sm text-brand-mute">
          Points everything at the real race, <strong>Sean McCann vs Bill Huizenga, U.S. House Michigan&apos;s 4th District</strong>:
          compliance profile (FEC C00911784, primary Aug 4 / general Nov 3, 180,000-vote goal), one active campaign,
          the MI-04 watchlist and listening sources, the money tracker (Huizenga C00459297), Local Press narrowed to the
          four regions that cover the district, and a synthetic MI-04 voter universe if this workspace has no voter file yet.
          Seeded demo content is removed. Safe to run more than once.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type MI-04 to confirm"
            className="max-w-[220px]" data-testid="rescope-confirm-input" />
          <Button onClick={rescope} disabled={rescoping || confirm.trim().toUpperCase() !== "MI-04"}
            className="bg-brand-navy text-white hover:bg-brand-navy/90" data-testid="rescope-mi04-btn">
            {rescoping ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
            Re-scope to MI-04
          </Button>
        </div>
        {rescopeLog.length > 0 && (
          <pre className="mt-4 max-h-60 overflow-auto rounded-lg bg-slate-900 p-3 text-xs leading-relaxed text-slate-100" data-testid="rescope-log">
            {rescopeLog.join("\n")}
          </pre>
        )}
      </Card>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} onDone={refresh} />
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange, onDone }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext(""); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading"><Lock className="h-4 w-4" /> Change your password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5" data-testid="current-password-input" /></div>
          <div><Label>New password</Label><Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5" data-testid="new-password-input" /><p className="mt-1 text-xs text-brand-mute">At least 8 characters.</p></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !cur || !next} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="save-password-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------- Compliance ---------- */
function Compliance() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["profile"], queryFn: () => api.get("/settings/profile").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm({ ...data, election_date: data.election_date || "2026-11-03", gotv_goal: data.gotv_goal ?? 2000 }); }, [data]);
  if (!form) return null;
  const canEdit = hasRole("admin", "manager");

  const RACE_TYPES = [
    { v: "local", label: "Local / municipal" },
    { v: "state_house", label: "State House" },
    { v: "state_senate", label: "State Senate" },
    { v: "us_house", label: "US House / Congress (Michigan district)" },
    { v: "us_senate", label: "US Senate (Michigan, statewide)" },
    { v: "ballot_measure", label: "Ballot measure" },
  ];
  const FEDERAL_TYPES = ["us_house", "us_senate"];
  const isFederal = FEDERAL_TYPES.includes(form.race_type);
  const hasDistrict = ["us_house", "state_house", "state_senate"].includes(form.race_type);

  const ordinal = (n) => {
    const i = Number(n);
    if (!i) return "";
    const s = ["th", "st", "nd", "rd"];
    return `${i}${s[(i % 100 - 20) % 10] || s[i % 100] || s[0]}`;
  };
  const houseJurisdiction = (d) =>
    d ? `Michigan's ${ordinal(d)} Congressional District (Federal / FEC)`
      : "Michigan congressional district (Federal / FEC)";

  const setDistrict = (raw) => {
    const d = raw.replace(/\D/g, "").slice(0, 2);
    setForm((f) => (f.race_type === "us_house"
      ? { ...f, district: d, jurisdiction: houseJurisdiction(d), geo_focus: d ? `MI-${d.padStart(2, "0")}` : "" }
      : { ...f, district: d }));
  };

  const applyRaceType = (v) => {
    if (v === "us_senate") {
      setForm((f) => ({
        ...f, race_type: v, race_scope: "statewide",
        primary_date: f.primary_date || "2026-08-04",
        election_date: "2026-11-03",
        jurisdiction: "Michigan: Statewide (Federal / FEC)",
        dateline_city: f.dateline_city || "Lansing, MI",
        geo_focus: f.geo_focus || "Michigan, statewide",
      }));
      toast.info("US Senate preset applied. Primary Aug 4, general Nov 3, statewide scope, FEC compliance checks enabled");
    } else if (v === "us_house") {
      setForm((f) => {
        const d = (f.district || "").replace(/\D/g, "").slice(0, 2);
        return {
          ...f, race_type: v, race_scope: "district", district: d,
          primary_date: f.primary_date || "2026-08-04",
          election_date: "2026-11-03",
          jurisdiction: houseJurisdiction(d),
          geo_focus: f.geo_focus || (d ? `MI-${d.padStart(2, "0")}` : ""),
        };
      });
      toast.info("US House preset applied. Primary Aug 4, general Nov 3, district scope, FEC compliance checks enabled. Add your district number.");
    } else {
      setForm((f) => ({ ...f, race_type: v, race_scope: v === "ballot_measure" ? f.race_scope : "district" }));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/profile", {
        org_name: form.org_name || "", committee_name: form.committee_name || "",
        address: form.address || "", jurisdiction: form.jurisdiction || "",
        disclaimer: form.disclaimer || "", voice_notes: form.voice_notes || "",
        election_date: form.election_date || null,
        gotv_goal: form.gotv_goal === "" || form.gotv_goal == null ? null : Number(form.gotv_goal),
        race_type: form.race_type || "local",
        race_scope: form.race_scope || "district",
        district: (form.district || "").trim(),
        primary_date: form.primary_date || null,
        fec_committee_id: form.fec_committee_id || "",
        dateline_city: form.dateline_city || "",
        geo_focus: form.geo_focus || "",
      });
      toast.success("Compliance profile saved");
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["geo"] });
      qc.invalidateQueries({ queryKey: ["gotv"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const F = ({ k, label, ...p }) => (
    <div><Label>{label}</Label><Input value={form[k] || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`profile-${k}`} {...p} /></div>
  );

  return (
    <Card className="max-w-3xl space-y-4">
      <div className="flex items-center gap-2 rounded-lg bg-brand-blue/5 p-3 text-sm text-brand-navy">
        <ShieldCheck className="h-4 w-4" /> This disclaimer is auto-injected into all public content and email footers. It's a hard dependency before any send.
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <F k="org_name" label="Organization" />
        <F k="committee_name" label="Committee name" />
      </div>
      <F k="address" label="Committee address" />
      <F k="jurisdiction" label="Jurisdiction" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label>Race type</Label>
          <Select value={form.race_type || "local"} onValueChange={applyRaceType} disabled={!canEdit}>
            <SelectTrigger className="mt-1.5" data-testid="profile-race_type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RACE_TYPES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Race scope</Label>
          <Select value={form.race_scope || "district"} onValueChange={(v) => setForm({ ...form, race_scope: v })} disabled={!canEdit}>
            <SelectTrigger className="mt-1.5" data-testid="profile-race_scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="district">District / local</SelectItem>
              <SelectItem value="statewide">Statewide (all 83 counties)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {isFederal && (
        <div className="flex items-start gap-2 rounded-lg border border-brand-red/20 bg-brand-red/5 p-3 text-sm text-brand-ink" data-testid="fec-notice">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
          <span>
            Federal race: public communications must carry a &ldquo;Paid for by&rdquo; disclaimer in the FEC format (52 U.S.C. §30120).
            {form.race_type === "us_senate"
              ? " Personas, the sentiment map, and GOTV switch to statewide (region/county) mode."
              : " Personas, the sentiment map, and GOTV run district-wide, mapped by county."}
          </span>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {hasDistrict && (
          <div>
            <Label>District number</Label>
            <Input value={form.district || ""} disabled={!canEdit} onChange={(e) => setDistrict(e.target.value)}
              placeholder={form.race_type === "us_house" ? "04" : "40"} className="mt-1.5" data-testid="profile-district" />
            <p className="mt-1 text-[0.7rem] text-brand-mute">
              {form.race_type === "us_house" ? "Sets the jurisdiction line and the MI-xx geographic focus used in AI prompts." : "Used for targeting and prompt context."}
            </p>
          </div>
        )}
        {isFederal && <F k="fec_committee_id" label="FEC committee ID" placeholder="C00XXXXXX" />}
        <F k="dateline_city" label="Press dateline city" placeholder="Lansing, MI" />
        <F k="geo_focus" label="Geographic focus (used in AI prompts)" placeholder="Michigan, statewide" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <Label>Primary date</Label>
          <Input type="date" value={form.primary_date || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, primary_date: e.target.value })} className="mt-1.5" data-testid="profile-primary_date" />
        </div>
        <div>
          <Label>Election day (general)</Label>
          <Input type="date" value={form.election_date || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, election_date: e.target.value })} className="mt-1.5" data-testid="profile-election_date" />
        </div>
        <div>
          <Label>GOTV contact goal</Label>
          <Input type="number" min="0" value={form.gotv_goal ?? ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, gotv_goal: e.target.value })} className="mt-1.5" data-testid="profile-gotv_goal" />
        </div>
      </div>
      <div><Label>Required disclaimer</Label><Textarea value={form.disclaimer || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, disclaimer: e.target.value })} rows={2} className="mt-1.5" data-testid="profile-disclaimer" /></div>
      <div><Label>Campaign voice & tone</Label><Textarea value={form.voice_notes || ""} disabled={!canEdit} onChange={(e) => setForm({ ...form, voice_notes: e.target.value })} rows={3} className="mt-1.5" /></div>
      {canEdit && <Button onClick={save} disabled={busy} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="save-profile-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save profile</Button>}
    </Card>
  );
}

/* ---------- Consent log (legal acknowledgements + file attestations) ---------- */
function ConsentLog() {
  const { data } = useQuery({ queryKey: ["consentLog"], queryFn: () => api.get("/settings/consent-log").then((r) => r.data) });
  if (!data) return null;

  const exportCsv = async () => {
    try {
      const r = await api.get("/settings/consent-log?fmt=csv", { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href; a.download = "consent_log.csv"; a.click(); URL.revokeObjectURL(href);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="space-y-5" data-testid="consent-log-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-ink">Consent record</h3>
          <p className="mt-1 text-sm text-brand-mute">
            Who acknowledged the Privacy Policy and Terms of Use (version {data.label}), and the
            lawful-source attestation captured on every voter-file import.
          </p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="export-consent-log-btn">
          <ScrollText className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="rounded-lg border border-pos-border bg-pos-bg px-4 py-2">
          <p className="font-heading text-2xl font-extrabold text-pos-text" data-testid="consent-acknowledged-count">{data.acknowledged}</p>
          <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-brand-mute">Acknowledged</p>
        </div>
        <div className="rounded-lg border border-border px-4 py-2">
          <p className="font-heading text-2xl font-extrabold text-brand-mute" data-testid="consent-pending-count">{data.pending}</p>
          <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-brand-mute">Pending</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <Table data-testid="consent-users-table">
          <TableHeader>
            <TableRow><TableHead>Person</TableHead><TableHead>Role</TableHead><TableHead>Acknowledged</TableHead><TableHead>Version</TableHead><TableHead>IP</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {data.users.map((u) => (
              <TableRow key={u.email} data-testid={`consent-row-${u.email}`}>
                <TableCell>
                  <p className="font-medium text-brand-ink">{u.name || u.email}</p>
                  <p className="text-xs text-brand-mute">{u.email}</p>
                </TableCell>
                <TableCell className="text-xs text-brand-mute">{u.role_label}</TableCell>
                <TableCell className="text-xs">
                  {u.accepted_at
                    ? <span className="text-brand-ink">{new Date(u.accepted_at).toLocaleString()}</span>
                    : <Badge variant="outline" className="border-brand-red/30 text-brand-red">Not yet</Badge>}
                </TableCell>
                <TableCell className="text-xs text-brand-mute">{u.version || "-"}</TableCell>
                <TableCell className="text-xs text-brand-mute">{u.ip || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-brand-ink">Voter-file attestations</h4>
        {data.attestations.length === 0 ? (
          <p className="mt-2 text-sm text-brand-mute" data-testid="no-attestations">
            No attested imports yet. The next voter-file import will be recorded here.
          </p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-xl border border-border">
            <Table data-testid="consent-attestations-table">
              <TableHeader>
                <TableRow><TableHead>File</TableHead><TableHead>Rows</TableHead><TableHead>Attested by</TableHead><TableHead>When</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {data.attestations.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium text-brand-ink">{a.filename}</TableCell>
                    <TableCell className="text-xs text-brand-mute">{(a.rows || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-xs text-brand-mute">{a.by}</TableCell>
                    <TableCell className="text-xs text-brand-mute">{a.at ? timeAgo(a.at) : "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------- Campaigns ---------- */
function Campaigns() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["campaigns"], queryFn: () => api.get("/settings/campaigns").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "candidate", candidate: "", opponent: "", office: "" });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try { await api.post("/settings/campaigns", form); toast.success("Campaign added"); qc.invalidateQueries({ queryKey: ["campaigns"] }); setOpen(false); setForm({ name: "", type: "candidate", candidate: "", opponent: "", office: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/settings/campaigns/${id}`, { status }); toast.success("Active campaign updated"); qc.invalidateQueries({ queryKey: ["campaigns"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.name}"?`)) return;
    try { await api.delete(`/settings/campaigns/${c.id}`); toast.success("Campaign removed"); qc.invalidateQueries({ queryKey: ["campaigns"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      {hasRole("admin", "manager") && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="new-campaign-btn"><Plus className="mr-2 h-4 w-4" /> New campaign</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New campaign</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="campaign-name-input" /></div>
              <div><Label>Type</Label><Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="candidate">Candidate</SelectItem><SelectItem value="ballot_measure">Ballot measure</SelectItem></SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3"><div><Label>Candidate</Label><Input value={form.candidate} onChange={(e) => setForm({ ...form, candidate: e.target.value })} className="mt-1.5" /></div><div><Label>Opponent</Label><Input value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} className="mt-1.5" /></div></div>
              <div><Label>Office / measure</Label><Input value={form.office} onChange={(e) => setForm({ ...form, office: e.target.value })} className="mt-1.5" /></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-brand-red text-white" data-testid="save-campaign-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {data?.map((c) => (
          <Card key={c.id} data-testid={`campaign-card-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-heading font-bold text-brand-ink">{c.name}</h3>
              <div className="flex items-center gap-1.5">
                {c.status === "active" && <Badge className="bg-brand-red text-white hover:bg-brand-red">Active</Badge>}
                <Badge variant="outline" className="capitalize">{c.type.replace("_", " ")}</Badge>
              </div>
            </div>
            <p className="mt-1 text-sm text-brand-mute">{c.office}</p>
            {c.candidate && <p className="mt-2 text-sm text-brand-ink">{c.candidate}{c.opponent && <span className="text-brand-mute"> vs {c.opponent}</span>}</p>}
            {hasRole("admin", "manager") && (
              <div className="mt-3 flex gap-2">
                {c.status !== "active" && (
                  <Button size="sm" variant="outline" data-testid={`activate-campaign-${c.id}`} onClick={() => setStatus(c.id, "active")}>Make active</Button>
                )}
                <Button size="sm" variant="ghost" className="text-brand-red hover:bg-brand-red/10" data-testid={`delete-campaign-${c.id}`} onClick={() => remove(c)}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------- Integrations ---------- */
function Integrations() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["integrations"], queryFn: () => api.get("/settings/integrations").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ provider: "SendGrid", type: "email", api_key: "" });

  const connect = async () => {
    try { await api.post("/settings/integrations", { ...form, api_key: form.api_key || undefined }); toast.success(`${form.provider} updated`); qc.invalidateQueries({ queryKey: ["integrations"] }); setOpen(false); setForm({ provider: "SendGrid", type: "email", api_key: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const revoke = async (id) => { try { await api.post(`/settings/integrations/${id}/revoke`); toast.success("Revoked"); qc.invalidateQueries({ queryKey: ["integrations"] }); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4">
      {hasRole("admin") && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="connect-integration-btn"><KeyRound className="mr-2 h-4 w-4" /> Connect API key</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Connect a provider</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Provider</Label><Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="mt-1.5" list="integration-providers" data-testid="integration-provider-input" />
                <datalist id="integration-providers">
                  <option value="SendGrid" /><option value="Slack" /><option value="NewsData.io" /><option value="FEC" /><option value="Meta Ad Library" />
                </datalist>
                <p className="mt-1 text-[0.7rem] text-brand-mute">Use the exact name shown on the cards below so the app picks the key up.</p>
              </div>
              <div><Label>API key</Label><Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="Stored KMS-encrypted, never in plaintext" className="mt-1.5" data-testid="integration-key-input" /></div>
              <p className="text-xs text-brand-mute">Keys are encrypted at rest and never logged. Only a masked preview is shown.</p>
            </div>
            <DialogFooter><Button onClick={connect} className="bg-brand-red text-white" data-testid="save-integration-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data?.map((it) => (
          <Card key={it.id}>
            <div className="flex items-center justify-between">
              <p className="font-heading font-bold text-brand-ink">{it.provider}</p>
              {it.status === "connected" ? <Badge className="bg-pos-bg text-pos-text"><CheckCircle2 className="mr-1 h-3 w-3" /> Connected</Badge> : <Badge className="bg-slate-100 text-slate-500"><XCircle className="mr-1 h-3 w-3" /> Not connected</Badge>}
            </div>
            <p className="mt-1 text-xs capitalize text-brand-mute">{it.type.replace("_", " ")}{it.masked_key && ` · ${it.masked_key}`}</p>
            {hasRole("admin") && it.status === "connected" && <Button variant="outline" size="sm" className="mt-3" onClick={() => revoke(it.id)} data-testid={`revoke-${it.id}`}><Trash2 className="mr-1.5 h-3.5 w-3.5 text-brand-red" /> Revoke</Button>}
            {hasRole("admin") && it.status !== "connected" && <Button variant="outline" size="sm" className="mt-3" onClick={() => { setForm({ provider: it.provider, type: it.type, api_key: "" }); setOpen(true); }} data-testid={`connect-${it.id}`}><KeyRound className="mr-1.5 h-3.5 w-3.5" /> Connect</Button>}
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------- Team ---------- */
function Team() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/settings/users").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "staff", password: "" });

  const save = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("All fields required");
    try { await api.post("/settings/users", form); toast.success("Teammate added"); qc.invalidateQueries({ queryKey: ["users"] }); setOpen(false); setForm({ name: "", email: "", role: "staff", password: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Remove this user?")) return; try { await api.delete(`/settings/users/${id}`); toast.success("Removed"); qc.invalidateQueries({ queryKey: ["users"] }); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="new-user-btn"><Plus className="mr-2 h-4 w-4" /> Add teammate</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Add teammate</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" /></div>
            <div><Label>Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent>{["admin", "manager", "staff", "observer"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Temp password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="user-password-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-brand-red text-white" data-testid="save-user-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Card className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {data?.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium text-brand-ink">{u.name}</TableCell>
                <TableCell className="text-brand-mute">{u.email}</TableCell>
                <TableCell><span className={cn("rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize", ROLE_BADGE[u.role])}>{u.role_label || u.role}</span></TableCell>
                <TableCell><Button variant="ghost" size="icon" onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4 text-brand-red" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ---------- Activity (audit) ---------- */
function Activity() {
  const { data } = useQuery({ queryKey: ["audit"], queryFn: () => api.get("/dashboard/audit").then((r) => r.data) });
  return (
    <Card className="p-0">
      <Table>
        <TableHeader><TableRow><TableHead>Action</TableHead><TableHead>Actor</TableHead><TableHead>Target</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
        <TableBody>
          {data?.map((a) => (
            <TableRow key={a.id}>
              <TableCell className="font-medium text-brand-ink">{titleCase(a.action)}</TableCell>
              <TableCell className="text-brand-mute">{a.actor_email}</TableCell>
              <TableCell className="text-xs text-brand-mute">{a.target_type}</TableCell>
              <TableCell className="text-xs text-brand-mute">{timeAgo(a.timestamp)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ---------- AI Cost metering ---------- */
function Cost() {
  const { data } = useQuery({ queryKey: ["metering"], queryFn: () => api.get("/dashboard/metering").then((r) => r.data) });
  if (!data) return null;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card><p className="text-xs font-semibold uppercase tracking-wider text-brand-mute">Estimated spend</p><p className="mt-2 font-heading text-3xl font-extrabold text-brand-ink">{fmtMoney(data.total_cost_estimate)}</p></Card>
        <Card><p className="text-xs font-semibold uppercase tracking-wider text-brand-mute">Total tokens</p><p className="mt-2 font-heading text-3xl font-extrabold text-brand-ink">{data.total_tokens.toLocaleString()}</p></Card>
        <Card><p className="text-xs font-semibold uppercase tracking-wider text-brand-mute">LLM calls</p><p className="mt-2 font-heading text-3xl font-extrabold text-brand-ink">{data.calls}</p></Card>
      </div>
      <Card>
        <h3 className="font-heading font-bold text-brand-ink">By provider</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {Object.entries(data.by_provider).map(([p, v]) => (
            <div key={p} className="rounded-lg border border-border p-3">
              <p className="font-semibold capitalize text-brand-navy">{p}</p>
              <p className="text-sm text-brand-mute">{v.calls} calls · {v.tokens.toLocaleString()} tok</p>
              <p className="font-heading text-lg font-bold text-brand-ink">{fmtMoney(v.cost)}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-brand-mute">Estimates based on token counts; metered on every gateway call.</p>
      </Card>
    </div>
  );
}
